import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, fmtPrice, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import LocationPickerMap from "@/components/LocationPickerMap";
import { AlertCircle, Banknote, Check, Home, Loader2, MapPin, Minus, Navigation, Plus, ShoppingBag, Store, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PRODUCT_FALLBACK = "/image-placeholder.svg";

export default function Checkout() {
  const navigate = useNavigate();
  const { cart, setQty, clearCart, subtotal } = useCart();
  const { user } = useAuth();
  const [address, setAddress] = useState(user?.address || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [note, setNote] = useState("");
  const [placing, setPlacing] = useState(false);
  const [destCoords, setDestCoords] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("other");
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);

  useEffect(() => {
    api.get("/profile/addresses")
      .then(({ data }) => {
        setSavedAddresses(data);
        const preferred = data.find((item) => item.is_default) || data[0];
        if (preferred) {
          setSelectedAddressId(preferred.id);
          setAddress(preferred.address);
          if (preferred.lat != null && preferred.lng != null) {
            setDestCoords({ lat: preferred.lat, lng: preferred.lng });
          }
        }
      })
      .catch(() => {});
  }, []);

  if (!cart) {
    return (
      <div className="mx-auto max-w-xl rounded-[34px] bg-muted py-20 text-center" data-testid="empty-cart">
        <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-brand text-brand-foreground shadow-brand">
          <ShoppingBag className="h-8 w-8" />
        </div>
        <h1 className="mt-6 text-2xl font-extrabold">سلتك فارغة</h1>
        <p className="mt-2 text-sm text-muted-foreground">اكتشف المتاجر والمنتجات القريبة منك.</p>
        <Button onClick={() => navigate("/app")} className="mt-6 h-12 rounded-full px-7 font-extrabold">تصفّح المتاجر</Button>
      </div>
    );
  }

  const total = subtotal + cart.delivery_fee;

  const selectSavedAddress = (item) => {
    setSelectedAddressId(item.id);
    setAddress(item.address);
    setDestCoords(item.lat != null && item.lng != null ? { lat: item.lat, lng: item.lng } : null);
  };

  const selectOtherAddress = () => {
    setSelectedAddressId("other");
    if (selectedAddressId !== "other") {
      setAddress("");
      setDestCoords(null);
    }
    setLocationPickerOpen(true);
  };

  const AddressIcon = ({ label }) => {
    if (/بيت|منزل/.test(label)) return <Home className="h-4 w-4" />;
    if (/محل|عمل|مكتب/.test(label)) return <Store className="h-4 w-4" />;
    return <MapPin className="h-4 w-4" />;
  };

  const placeOrder = async () => {
    if (user?.phone_verified === false) {
      toast.error("أكد رقم WhatsApp قبل تأكيد الطلب");
      return;
    }
    if (!address.trim() || !phone.trim()) {
      toast.error("أدخل العنوان ورقم الهاتف");
      return;
    }
    if (selectedAddressId === "other" && !destCoords) {
      toast.error("حدد موقع التوصيل على الخريطة وأكد أنه صحيح");
      setLocationPickerOpen(true);
      return;
    }
    setPlacing(true);
    try {
      await api.post("/orders", {
        shop_id: cart.shop_id,
        items: cart.items,
        delivery_address: address,
        phone,
        note,
        payment_method: "cash",
        dest_lat: destCoords?.lat ?? null,
        dest_lng: destCoords?.lng ?? null,
      });
      clearCart();
      toast.success("تم إرسال طلبك بنجاح!");
      navigate("/app/orders");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    } finally {
      setPlacing(false);
    }
  };

  const fieldClass = "h-12 rounded-[16px] border-0 bg-muted px-4 shadow-none focus-visible:ring-2 focus-visible:ring-primary/20";

  return (
    <div data-testid="checkout-page" className="mx-auto max-w-6xl pb-[calc(6rem+env(safe-area-inset-bottom))] md:pb-0">
      <div className="mb-5 md:mb-7">
        <span className="hidden text-[10px] font-extrabold uppercase tracking-[0.16em] text-primary md:block">خطوتك الأخيرة</span>
        <h1 className="mt-1 text-xl font-extrabold tracking-[-0.03em] sm:text-4xl">إتمام الطلب</h1>
        <p className="mt-1 text-xs text-muted-foreground sm:mt-2 sm:text-sm">طلبك من <span className="font-bold text-foreground">{cart.shop_name}</span></p>
      </div>

      {user?.phone_verified === false && (
        <div className="mb-6 flex flex-col gap-4 rounded-[22px] border border-amber-500/20 bg-amber-500/[0.06] p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
            <div>
              <p className="font-extrabold">بقي تأكيد WhatsApp قبل إرسال الطلب</p>
              <p className="mt-1 text-sm text-muted-foreground">يمكنك مراجعة سلتك الآن، ولن يتم تأكيد الطلب قبل توثيق رقمك.</p>
            </div>
          </div>
          <Button onClick={() => navigate(`/auth?mode=${user.phone ? "verify" : "change-phone"}`)} className="shrink-0 rounded-full px-5 font-bold">
            {user.phone ? "تأكيد الرقم" : "إضافة رقم WhatsApp"}
          </Button>
        </div>
      )}

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(360px,.75fr)] lg:gap-8">
        <div>
          <div className="space-y-3">
            {cart.items.map((item) => (
              <div
                key={item.product_id}
                data-testid={`cart-item-${item.product_id}`}
                className="flex items-center gap-3 rounded-[24px] bg-card p-3 shadow-card sm:gap-4 sm:p-4"
              >
                <img
                  src={item.image_path ? fileUrl(item.image_path) : PRODUCT_FALLBACK}
                  alt={item.name}
                  className="h-20 w-20 shrink-0 rounded-[18px] object-cover sm:h-24 sm:w-24"
                />
                <div className="min-w-0 flex-1">
                  <h3 className="line-clamp-1 text-sm font-extrabold sm:text-base">{item.name}</h3>
                  <p className="font-num mt-1 text-sm font-extrabold text-muted-foreground">{fmtPrice(item.price)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5 rounded-full bg-muted p-1 sm:gap-2">
                  <button data-testid={`dec-${item.product_id}`} onClick={() => setQty(item.product_id, item.qty - 1)} className="grid h-8 w-8 place-items-center rounded-full bg-card text-foreground shadow-sm">
                    {item.qty === 1 ? <Trash2 className="h-3.5 w-3.5 text-destructive" /> : <Minus className="h-4 w-4" />}
                  </button>
                  <span className="w-5 text-center text-sm font-extrabold" data-testid={`qty-${item.product_id}`}>{item.qty}</span>
                  <button data-testid={`inc-${item.product_id}`} onClick={() => setQty(item.product_id, item.qty + 1)} className="grid h-8 w-8 place-items-center rounded-full bg-brand text-brand-foreground active:scale-90">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-[28px] bg-card p-5 shadow-card sm:p-6">
            <div className="mb-5 flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-2xl bg-brand text-brand-foreground"><MapPin className="h-4 w-4" /></div>
              <div>
                <h2 className="font-extrabold">معلومات التوصيل</h2>
                <p className="mt-0.5 text-[10px] text-muted-foreground">أدخل المكان الذي تريد استلام الطلب فيه</p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-3 sm:col-span-2">
                <div className="flex items-center justify-between gap-3">
                  <Label className="text-xs font-extrabold">اختر عنوان التوصيل</Label>
                  {savedAddresses.length > 0 && <button type="button" onClick={() => navigate("/app/profile")} className="text-[10px] font-extrabold text-primary hover:underline">إدارة العناوين</button>}
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {savedAddresses.map((item) => {
                    const selected = selectedAddressId === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => selectSavedAddress(item)}
                        className={`flex items-start gap-3 rounded-[18px] border p-3 text-start transition-colors ${selected ? "border-primary bg-primary/[0.055]" : "border-border bg-background hover:border-primary/30"}`}
                      >
                        <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${selected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}><AddressIcon label={item.label} /></span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5 text-xs font-extrabold">{item.label}{item.is_default && <span className="text-[8px] text-primary">افتراضي</span>}</span>
                          <span className="mt-1 block line-clamp-2 text-[10px] leading-4 text-muted-foreground">{item.address}</span>
                        </span>
                        {selected && <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    data-testid="select-other-location"
                    aria-pressed={selectedAddressId === "other"}
                    onClick={selectOtherAddress}
                    className={`flex items-center gap-3 rounded-[18px] border p-3 text-start transition-colors ${selectedAddressId === "other" ? "border-primary bg-primary/[0.055]" : "border-border bg-background hover:border-primary/30"}`}
                  >
                    <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${selectedAddressId === "other" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}><MapPin className="h-4 w-4" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-xs font-extrabold">عنوان آخر</span>
                      <span className="mt-1 block text-[10px] text-muted-foreground">تحديد الموقع على الخريطة</span>
                    </span>
                    {selectedAddressId === "other" && destCoords && <Check className="h-4 w-4 shrink-0 text-primary" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-xs font-extrabold">العنوان</Label>
                <Input data-testid="checkout-address" value={address} onChange={(event) => { setAddress(event.target.value); if (selectedAddressId !== "other") { setSelectedAddressId("other"); setDestCoords(null); } }} placeholder="الحي والشارع وأقرب نقطة دالة" className={fieldClass} />
                {selectedAddressId === "other" && (
                  <button type="button" onClick={() => setLocationPickerOpen(true)} className={`mt-2 flex w-full items-center gap-3 rounded-[16px] px-4 py-3 text-start text-xs font-bold transition-colors ${destCoords ? "bg-success/10 text-success" : "bg-primary/10 text-primary"}`}>
                    <Navigation className="h-4 w-4 shrink-0" />
                    <span className="flex-1">{destCoords ? "تم تأكيد موقع التوصيل على الخريطة" : "حدد موقع التوصيل على الخريطة"}</span>
                    <span className="text-[10px] underline">{destCoords ? "تعديل" : "فتح الخريطة"}</span>
                  </button>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-extrabold">رقم الهاتف</Label>
                <Input data-testid="checkout-phone" value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="09xxxxxxxx" dir="ltr" className={`${fieldClass} text-start`} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-extrabold">ملاحظات (اختياري)</Label>
                <Textarea data-testid="checkout-note" value={note} onChange={(event) => setNote(event.target.value)} placeholder="تعليمات إضافية للسائق" className="min-h-12 resize-none rounded-[16px] border-0 bg-muted px-4 shadow-none focus-visible:ring-2 focus-visible:ring-primary/20" />
              </div>
            </div>
          </div>
        </div>

        <aside className="brand-panel rounded-[30px] p-5 text-white shadow-brand sm:p-6 lg:sticky lg:top-24">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-extrabold">ملخص الطلب</h2>
            <div className="grid h-10 w-10 place-items-center rounded-full bg-white/10"><ShoppingBag className="h-4 w-4" /></div>
          </div>
          <div className="mt-6 space-y-3 text-sm">
            <div className="flex justify-between gap-3 text-white/60"><span>المجموع الفرعي</span><span className="font-num text-white">{fmtPrice(subtotal)}</span></div>
            <div className="flex justify-between gap-3 text-white/60"><span>رسوم التوصيل</span><span className="font-num text-white">{fmtPrice(cart.delivery_fee)}</span></div>
          </div>
          <div className="mt-6 flex items-end justify-between gap-3 border-t border-white/10 pt-5">
            <span className="text-sm font-bold text-white/65">الإجمالي</span>
            <span className="font-num text-2xl font-extrabold text-white">{fmtPrice(total)}</span>
          </div>
          <div className="mt-5 flex items-center gap-2 rounded-[18px] bg-white/10 p-3 text-xs text-white/70">
            <Banknote className="h-4 w-4 text-white" />
            <span>الدفع نقدًا عند الاستلام</span>
          </div>
          <Button data-testid="place-order-btn" onClick={placeOrder} disabled={placing || user?.phone_verified === false} className="mt-5 hidden h-14 w-full rounded-full bg-white text-sm font-extrabold text-primary hover:bg-white/90 md:flex">
            {placing ? <Loader2 className="h-5 w-5 animate-spin" /> : `تأكيد الطلب • ${fmtPrice(total)}`}
          </Button>
        </aside>
      </div>
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 p-3 backdrop-blur-xl md:hidden" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.75rem)" }}>
        <div className="mx-auto flex max-w-xl items-center gap-3">
          <div className="min-w-0 flex-1">
            <span className="block text-[10px] text-muted-foreground">الإجمالي · نقدًا عند الاستلام</span>
            <span className="font-num text-sm font-extrabold text-foreground">{fmtPrice(total)}</span>
          </div>
          <Button data-testid="place-order-btn-mobile" onClick={placeOrder} disabled={placing || user?.phone_verified === false} className="h-12 shrink-0 rounded-2xl px-5 text-xs font-extrabold">
            {placing ? <Loader2 className="h-5 w-5 animate-spin" /> : "تأكيد الطلب"}
          </Button>
        </div>
      </div>
      <LocationPickerMap
        open={locationPickerOpen}
        onOpenChange={setLocationPickerOpen}
        value={selectedAddressId === "other" ? destCoords : null}
        onConfirm={(coords) => {
          setSelectedAddressId("other");
          setDestCoords(coords);
          toast.success("تم تأكيد موقع التوصيل");
        }}
      />
    </div>
  );
}
